import { useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL === '/api' ? '' : API_URL;

interface ActivityTrackerOptions {
  page: string;
  activity?: string;
}

export const useActivityTracker = ({ page, activity = 'Viewing' }: ActivityTrackerOptions) => {
  const lastActivityRef = useRef<string>('');
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const updateActivity = useCallback(async (newActivity?: string) => {
    const activityToSend = newActivity || activity;
    
    // Skip if same activity
    if (lastActivityRef.current === `${page}-${activityToSend}`) {
      return;
    }
    
    lastActivityRef.current = `${page}-${activityToSend}`;
    
    try {
      await axios.post(
        `${API_BASE}/api/user/activity`,
        { page, activity: activityToSend },
        { withCredentials: true }
      );
    } catch (error) {
      // Silently fail - activity tracking is not critical
    }
  }, [page, activity]);

  const sendHeartbeat = useCallback(async () => {
    try {
      await axios.post(
        `${API_BASE}/api/user/heartbeat`,
        {},
        { withCredentials: true }
      );
    } catch (error) {
      // Silently fail
    }
  }, []);

  useEffect(() => {
    // Initial activity update
    updateActivity();

    // Set up heartbeat interval (every 60 seconds)
    heartbeatIntervalRef.current = setInterval(sendHeartbeat, 60000);

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [updateActivity, sendHeartbeat]);

  return { updateActivity };
};

export default useActivityTracker;
